// IE 2026 11 03 / ODOO-350449 (заказ Игоря: объединение зон групп складов G1–G5; BE n_by_zone #1013)
// Режимы групповых строк запаса G1–G5 (эскиз stock5.html): как строка группы показывает уровни своих листей. Подключение — одна строка в
// STOCK_GROUP_MODES (utils/stockCells.js: ...stockGroupModes2_modes()), опции — fe.ux.stockGroupMode g1…g5 и fe.ux.stockGroupCalm (displayOptions).
//
// Источник состава группы:
//   - дети строки загружены и все — листья (у модели нет n_by_zone, leaves ≤ 1) — зоны листьев во времени по их ступенькам Z5 (как в эскизе «розгорнуто»);
//   - иначе (свёрнуто, дети — подгруппы, детей не видно) — n_by_zone ответа (BE ie-dm7 #1013): {zone: число листов} на конец месяца, нули опущены.
//     Половин месяца нет — состав постоянен на весь месяц; переходов внутри месяца в этом пути нет;
//   - нет n_by_zone и листьев > 1 (старый бэк) — режим F (как до G).
// Приглушение (fe.ux.stockGroupCalm, def true): Replenishment на группах серый; толщина и прозрачность цвета ∝ доле листьев зоны.
// Итог (корень строки запаса, nb.total), как у F: цвет держит только OOS (∝ доле); Critical и Over-norm — серым, у G2 нет полосы разброса —
// на итогах ничего не «горит» постоянно.
// ВАЖНО (цикл импорта со stockCells.js): на верхнем уровне модуля импорты stockCells не трогать — только внутри функций.
import { W, SPLIT, ZB, H, Z5L, ZRANK, STOCK_COLORS, STOCK_VIEWS, STOCK_GROUP_MODES, stock_vAt, stock_zoneOf, stock_zoneNorm, stock_segs, stock_yMap, stock_zoneTitle } from './stockCells'

export const STOCK_GROUP_CALM_FLAG = 'fe.ux.stockGroupCalm'
export const STOCK_GROUP_CALM_DEFAULT = true
export const STOCK_GROUP_G_IDS = ['g1', 'g2', 'g3', 'g4', 'g5']
export const G_THREADS_MAX = 12   // G3 по листьям — не больше 12 нитей, иначе нить на зону
const KS = 44                     // отсчётов на месяц (кривые G3 / G4 / G5)

const isObj = (o) => !!o && typeof o === 'object' && !Array.isArray(o)
const f1 = (v) => Math.round(v * 10) / 10
const r2 = (v) => Math.round(v * 100) / 100
const LV = { oos: 0, min: 1, opt: 1, ok: 2, max: 3, excess: 3, no: 2 }   // уровень Z5: OOS < Low < Norm < High
const LVY = { 0: 24.4, 1: 18, 2: 12, 3: 6 }
const YC = { 0: 19.4, 1: 16, 2: 12.5, 3: 9 }                              // G4: нити у середины
const ZORD = ['oos', 'min', 'opt', 'ok', 'max', 'excess', 'no']
const worse = (a, b) => LV[a] < LV[b] || (LV[a] === LV[b] && ZRANK[a] < ZRANK[b])
const better = (a, b) => LV[a] > LV[b] || (LV[a] === LV[b] && a === 'excess' && b !== 'excess')

// цвет с прозрачностью: '#rrggbb' → rgba(…, op); 'rgba(…, a)' → a × op
export function stockG_rgba(c, op = 1) {
    const o = Math.max(0, Math.min(1, Number(op)))
    if (!(o < 1)) return c
    const s = String(c || '')
    let m = /^#([0-9a-f]{6})$/i.exec(s)
    if (m) { const n = parseInt(m[1], 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${r2(o)})` }
    m = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/i.exec(s)
    if (m) return `rgba(${m[1]},${m[2]},${m[3]},${r2(Number(m[4]) * o)})`
    return s
}
// цвет зоны на группе: calm — Replenishment серый; итог — цвет только у OOS
function hue(z, calm, total) {
    const C = STOCK_COLORS
    if (z === 'oos') return C.oos
    if (z === 'min') return total ? C.gray : C.min
    if (z === 'opt') return calm || total ? C.gray : C.min
    if (z === 'excess') return total ? C.gray : C.in
    return z === 'no' ? C.no : C.gray
}
const isAlertHue = (c) => c === STOCK_COLORS.oos || c === STOCK_COLORS.min

// ---------- состав группы ----------
// n_by_zone модели → { cnt: {zone: n}, n } | null (неизвестен: нет поля и листьев > 1)
export function stockG_counts(cell) {
    const cnt = {}
    let sum = 0
    if (cell && isObj(cell.nbz)) {
        for (const [k, v] of Object.entries(cell.nbz)) { const z = stock_zoneNorm(k), n = Number(v); if (z && n > 0) { cnt[z] = (cnt[z] || 0) + n; sum += n } }
    }
    if (!sum) {
        if (!cell || Number(cell.leaves) > 1) return null
        const z = cell.zone || stock_zoneOf(cell.endRaw, cell.th, true)
        cnt[z] = 1; sum = 1
    }
    return { cnt, n: Math.max(sum, Number(cell.leaves) || 0) }
}
// листья-дети (загружены, все — листья) | null
function leafKids(nb) {
    const k = nb && Array.isArray(nb.kids) && nb.kids.length && !nb.collapsed ? nb.kids : null
    if (!k || !k.every(q => q && !isObj(q.nbz) && !(Number(q.leaves) > 1))) return null
    return k
}
// ступенька листа: отрезки [x0, x1, zone], последний — зона ответа (как sumSegs вида Z5)
function segsOf(m) {
    const ts = new Set([0, SPLIT, 1])
    for (let k = 1; k < 22; k++) ts.add(k / 22)
    for (const [t0, t1] of [[0, SPLIT], [SPLIT, 1]]) {
        const a = stock_vAt(m, t0), b = stock_vAt(m, t1)
        for (const L of [0, ...m.th]) if ((a - L) * (b - L) < 0) ts.add(t0 + (L - a) / (b - a) * (t1 - t0))
    }
    const last = m.zoneLast !== undefined ? m.zoneLast : m.zone
    return stock_segs((t) => stock_zoneOf(stock_vAt(m, t), m.th, true), [...ts].sort((a, b) => a - b), last)
}
function zoneAtX(segs, x) {
    for (const s of segs) if (x >= s[0] - 1e-6 && x <= s[1] + 1e-6) return s[2]
    return segs.length ? segs[segs.length - 1][2] : 'no'
}
const countOf = (zs) => { const c = {}; for (const z of zs) c[z] = (c[z] || 0) + 1; return c }
// интервалы месяца с постоянным составом: { leafwise, n, list: [{ x0, x1, cnt, zs | null }], zAt: [x → zone] | null } | null
export function stockG_intervals(cell, nb) {
    const kids = leafKids(nb)
    if (kids) {
        const segs = kids.map(segsOf), xs = new Set([0, W])
        for (const s of segs) for (const [x0, x1] of s) { xs.add(x0); xs.add(x1) }
        const X = [...xs].sort((a, b) => a - b), list = []
        for (let j = 1; j < X.length; j++) {
            if (X[j] - X[j - 1] < 1e-6) continue
            const xm = (X[j - 1] + X[j]) / 2, zs = segs.map(s => zoneAtX(s, xm)), key = zs.join()
            const p = list[list.length - 1]
            if (p && p.key === key) p.x1 = X[j]; else list.push({ x0: X[j - 1], x1: X[j], zs, key })
        }
        for (const iv of list) iv.cnt = countOf(iv.zs)
        return { leafwise: true, n: kids.length, list, zAt: segs.map(s => (x) => zoneAtX(s, x)) }
    }
    const b = stockG_counts(cell)
    if (!b) return null
    return { leafwise: false, n: b.n, list: [{ x0: 0, x1: W, cnt: b.cnt, zs: null }], zAt: null }
}
// края состава: low — худшая зона, high — лучшая, sl / sh — доли листьев на их уровнях
export function stockG_env(cnt, n) {
    let l = null, h = null
    for (const z of ZORD) if (cnt[z] > 0) { if (!l || worse(z, l)) l = z; if (!h || better(z, h)) h = z }
    if (!l) { l = 'no'; h = 'no' }
    const lvN = (lv) => ZORD.filter(z => LV[z] === lv).reduce((s, z) => s + (cnt[z] || 0), 0)
    const N = Math.max(1, n)
    return { l, h, sl: Math.min(1, lvN(LV[l]) / N), sh: Math.min(1, lvN(LV[h]) / N) }
}
const envAtOf = (iv) => (x) => { for (const p of iv.list) if (x >= p.x0 - 1e-6 && x <= p.x1 + 1e-6) return p; return iv.list[iv.list.length - 1] }

// ---------- общие слои ----------
const baseLine = () => ({ k: 'line', x1: 0, y1: ZB, x2: W, y2: ZB, stroke: STOCK_COLORS.base, sw: 0.7 })
const hline = (x0, x1, y, stroke, sw, op = 1) => ({ k: 'line', x1: f1(x0), y1: y, x2: f1(x1), y2: y, stroke, sw: r2(sw), op: op < 1 ? r2(op) : 1 })
const vline = (x, y1, y2) => ({ k: 'line', x1: f1(x), y1, x2: f1(x), y2, stroke: STOCK_COLORS.gray, sw: 0.6, op: 0.6 })
const poly = (pts, stroke, sw, op = 1) => ({ k: 'poly', pts: pts.map(p => [f1(p[0]), f1(p[1])]), stroke: stockG_rgba(stroke, op), sw: r2(sw) })
// сплошные куски отсчётов с одним ключем (стык — общей точкой); ключ null — разрыв
function piecesBy(samples, keyOf) {
    const out = []
    let cur = null
    for (let j = 0; j < samples.length; j++) {
        const sm = samples[j], k = keyOf(sm)
        if (k == null) { cur = null; continue }
        if (!cur || cur.k !== k) { cur = { k, pts: j && keyOf(samples[j - 1]) != null ? [[samples[j - 1].x, samples[j - 1].y]] : [], s: sm }; out.push(cur) }
        cur.pts.push([sm.x, sm.y])
    }
    return out.filter(p => p.pts.length > 1)
}
const smooth = (f, t, w = 0.07) => { let s = 0; for (let u = -4; u <= 4; u++) s += f(Math.min(1, Math.max(0, t + u * w / 4))); return s / 9 }
const fallbackF = (cell, nb, rs, size, ctx) => STOCK_GROUP_MODES.f(cell, nb, rs, size, ctx)
const flags = (nb, ctx) => ({ calm: !(ctx && ctx.calm === false), total: !!(nb && nb.total) })
const titleOf = (cell) => stock_zoneTitle(cell.zone)

// ---------- G1 «Наложение»: уровни Z5, где есть листья; толщина ∝ числу (calm — доле) листьев уровня ----------
function levelsOf(cnt, n, calm, total) {
    const by = {}
    for (const z of ZORD) if (cnt[z] > 0) { const l = LV[z]; (by[l] = by[l] || { l, n: 0, zs: [] }).n += cnt[z]; by[l].zs.push(z) }
    return Object.values(by).map(o => {
        let col = STOCK_COLORS.gray, op = 0.55
        if (o.l === 0) { col = hue('oos', calm, total); op = 0.85 } else if (o.l === 1) {
            const m = o.zs.includes('min')
            col = hue(m ? 'min' : 'opt', calm, total); op = m ? 0.85 : (calm ? 0.55 : 0.45)
        } else if (o.l === 3 && o.zs.includes('excess')) { col = hue('excess', calm, total); op = 0.6 }
        const s = Math.min(1, o.n / Math.max(1, n))
        const sw = calm ? 0.7 + 1.6 * s : Math.min(3, 0.9 + 0.75 * (o.n - 1))
        return { y: LVY[o.l], col, sw, op: calm ? op * (0.5 + 0.5 * s) : op }
    })
}
function g1(cell, nb, rs, size, ctx) {
    const iv = stockG_intervals(cell, nb)
    if (!iv) return fallbackF(cell, nb, rs, size, ctx)
    const { calm, total } = flags(nb, ctx), L = [baseLine()]
    iv.list.forEach((p, j) => {
        for (const o of levelsOf(p.cnt, iv.n, calm, total)) L.push(hline(p.x0, p.x1, o.y, o.col, o.sw, o.op))
        const nx = iv.list[j + 1]
        if (nx && p.zs) p.zs.forEach((z, k) => { const z2 = nx.zs[k]; if (LV[z] !== LV[z2]) L.push(vline(p.x1, LVY[LV[z]], LVY[LV[z2]])) })
    })
    return { layers: L, title: titleOf(cell) }
}

// ---------- G2 «Только границы»: нижний и верхний край состава, между ними — бледная полоса разброса ----------
function wLo(z, s, calm) {
    if (z === 'oos' || z === 'min') return calm ? { w: 0.9 + 1.4 * s, op: 0.5 + 0.5 * s } : { w: 2.2, op: 1 }
    if (z === 'opt') return { w: 1.2, op: calm ? 0.4 + 0.3 * s : 0.65 }
    return { w: 1.2, op: 1 }
}
function wHi(z, s, calm) { return z === 'excess' ? (calm ? { w: 0.8 + 1.0 * s, op: 0.55 + 0.45 * s } : { w: 1.6, op: 1 }) : { w: 1.2, op: 1 } }
const colHi = (z, calm, total) => (z === 'excess' ? hue('excess', calm, total) : STOCK_COLORS.gray)
function g2(cell, nb, rs, size, ctx) {
    const iv = stockG_intervals(cell, nb)
    if (!iv) return fallbackF(cell, nb, rs, size, ctx)
    const { calm, total } = flags(nb, ctx), C = STOCK_COLORS
    const P = []
    for (const p of iv.list) {
        const e = stockG_env(p.cnt, iv.n), q = P[P.length - 1]
        if (q && q.l === e.l && q.h === e.h && q.sl === e.sl && q.sh === e.sh) q.x1 = p.x1; else P.push(Object.assign({ x0: p.x0, x1: p.x1 }, e))
    }
    const band = [], lines = []
    P.forEach((p, j) => {
        const yl = LVY[LV[p.l]], yh = LVY[LV[p.h]]
        if (yl !== yh && !(calm && total)) band.push({ k: 'rect', x: f1(p.x0), y: yh, w: Math.max(0.6, f1(p.x1 - p.x0)), h: yl - yh, fill: C.fGray })
        if (p.l === 'oos') band.push({ k: 'rect', x: f1(p.x0), y: ZB, w: Math.max(0.6, f1(p.x1 - p.x0)), h: 4, fill: stockG_rgba(C.fOos, calm ? 0.35 + 0.65 * p.sl : 1) })
        const a = wLo(p.l, p.sl, calm)
        lines.push(hline(p.x0, p.x1, yl, hue(p.l, calm, total), a.w, a.op))
        if (yl !== yh) { const b = wHi(p.h, p.sh, calm); lines.push(hline(p.x0, p.x1, yh, colHi(p.h, calm, total), b.w, b.op)) }
        const nx = P[j + 1]
        if (nx) {
            const yl2 = LVY[LV[nx.l]], yh2 = LVY[LV[nx.h]]
            if (yl2 !== yl) lines.push(vline(p.x1, yl, yl2))
            if (yh2 !== yh && yh !== yl && yh2 !== yl2) lines.push(vline(p.x1, yh, yh2))
        }
    })
    return { layers: [baseLine(), ...band, ...lines], title: titleOf(cell) }
}

// ---------- G3 «Пучок»: нити стягиваются к оси пучка к середине месяца, у краёв — на своём уровне Z5 ----------
// по листьям (≤ G_THREADS_MAX): нить на лист, зона во времени; свёрнуто / по n_by_zone: нить на зону, толщина и прозрачность ∝ доле
const THREAD = { oos: [1.3, 1], min: [1.3, 1], opt: [1, 0.5], ok: [0.8, 0.55], no: [0.8, 0.55], max: [0.8, 0.55], excess: [0.9, 0.7] }
function g3(cell, nb, rs, size, ctx) {
    const iv = stockG_intervals(cell, nb)
    if (!iv) return fallbackF(cell, nb, rs, size, ctx)
    const { calm, total } = flags(nb, ctx)
    let th   // [{ f: t → zone, share }]
    if (iv.leafwise && iv.zAt.length <= G_THREADS_MAX) th = iv.zAt.map(zx => ({ f: (t) => zx(t * W), share: 0 }))
    else {
        const cnt = iv.leafwise ? countOf(iv.zAt.map(zx => zx(W))) : iv.list[0].cnt
        th = ZORD.filter(z => cnt[z] > 0).map(z => ({ f: () => z, share: Math.min(1, cnt[z] / Math.max(1, iv.n)) }))
    }
    const n = th.length, L = [baseLine()]
    if (!n) return { layers: L, title: titleOf(cell) }
    const yraw = th.map(o => (t) => LVY[LV[o.f(t)]])
    const mean = yraw.map(f => { let s = 0; for (let k = 0; k <= 20; k++) s += f(k / 20); return s / 21 })
    const rank = []
    mean.map((m, j) => [m, j]).sort((a, b) => a[0] - b[0] || a[1] - b[1]).forEach(([, j], r) => { rank[j] = r })
    const W8 = th.map(o => (o.share > 0 ? o.share : 1 / n))
    const Csum = (t) => yraw.reduce((s, g, j) => s + smooth(g, t) * W8[j], 0) / W8.reduce((s, w) => s + w, 0)
    th.forEach((o, j) => {
        const smp = []
        for (let k = 0; k <= KS; k++) {
            const t = k / KS, sp = 0.28 + 0.72 * Math.pow(Math.abs(2 * t - 1), 1.6), Cx = Csum(t)
            smp.push({ x: t * W, y: Cx + (smooth(yraw[j], t) - Cx) * sp + (rank[j] - (n - 1) / 2) * 1.3 * (1 - sp), z: o.f(t) })
        }
        for (const pc of piecesBy(smp, q => q.z)) {
            const z = pc.k === 'opt' && calm ? 'ok' : pc.k
            const [w0, op0] = THREAD[z] || THREAD.ok, s = o.share
            const w = s > 0 ? (calm ? w0 * (0.6 + 1.2 * s) : w0 + 0.5) : w0, op = s > 0 && calm ? op0 * (0.5 + 0.5 * s) : op0
            L.push(poly(pc.pts, hue(z, calm, total), w, op))
        }
    })
    return { layers: L, title: titleOf(cell) }
}

// ---------- G4 «Две нити»: верхняя (High / Over-norm) и нижняя (Low / OOS) у середины; всё в одной зоне — одна серая ----------
function g4(cell, nb, rs, size, ctx) {
    const iv = stockG_intervals(cell, nb)
    if (!iv) return fallbackF(cell, nb, rs, size, ctx)
    const { calm, total } = flags(nb, ctx), C = STOCK_COLORS, pAt = envAtOf(iv)
    const E = []
    for (let k = 0; k <= KS; k++) { const p = pAt(k / KS * W); E.push(stockG_env(p.cnt, iv.n)) }
    const at = (f) => (t) => f(E[Math.round(Math.min(1, Math.max(0, t)) * KS)])
    const top = [], bot = []
    for (let k = 0; k <= KS; k++) {
        const t = k / KS, e = E[k]
        const yt = smooth(at(q => YC[LV[q.h]]), t, 0.09), yb = smooth(at(q => YC[LV[q.l]]), t, 0.09)
        top.push({ x: t * W, y: yt, z: e.h, s: e.sh, same: Math.abs(yt - yb) < 0.25 }); bot.push({ x: t * W, y: yb, z: e.l, s: e.sl })
    }
    const bk = (s) => (calm ? Math.round(s * 4) : 0)
    const L = [baseLine()]
    for (const pc of piecesBy(top, q => (q.same ? null : (q.z === 'excess' ? 'excess' : 'g') + '|' + bk(q.s)))) {
        const ex = pc.k.startsWith('excess'), b = ex ? wHi('excess', pc.s.s, calm) : { w: 1.1, op: 1 }
        L.push(poly(pc.pts, ex ? hue('excess', calm, total) : C.gray, b.w, b.op))
    }
    for (const pc of piecesBy(bot, q => q.z + '|' + bk(q.s))) {
        const z = pc.k.split('|')[0]
        if (z === 'oos' || z === 'min' || z === 'opt') { const a = wLo(z, pc.s.s, calm); L.push(poly(pc.pts, hue(z, calm, total), a.w, a.op)) }
        else L.push(poly(pc.pts, z === 'excess' ? hue('excess', calm, total) : C.gray, 1.1, 1))
    }
    return { layers: L, title: titleOf(cell) }
}

// ---------- G5 «Сумма + ответвления»: ступенька суммы (вид ячейки), ус вниз при Critical / OOS части листьев, тёмный ус вверх при Over-norm ----------
// ус — на 3,3 единицы от линии суммы, у краёв своего отрезка плавно сливается с ней; толщина и прозрачность ∝ доле листьев (calm)
function g5(cell, nb, rs, size, ctx) {
    const iv = stockG_intervals(cell, nb)
    if (!iv) return fallbackF(cell, nb, rs, size, ctx)
    const { calm, total } = flags(nb, ctx), C = STOCK_COLORS
    const view = (ctx && ctx.view) || 'z5', base = (STOCK_VIEWS[view] || STOCK_VIEWS.z5)(cell, nb, rs, size)
    const pAt = envAtOf(iv), N = Math.max(1, iv.n)
    const smp = []
    for (let k = 0; k <= KS; k++) {
        const t = k / KS, v = stock_vAt(cell, t), sz = stock_zoneOf(v, cell.th, true)
        const ys = view === 'z5' || !STOCK_VIEWS[view] ? (Z5L[sz] ?? 12) : Math.max(0.8, Math.min(H - 0.6, stock_yMap(view, v, cell.th, sz, cell)))
        const c = pAt(t * W).cnt, al = c.oos ? 'oos' : (c.min ? 'min' : null)
        smp.push({ x: t * W, ys, al: al && worse(al, sz) ? al : null, sa: al ? Math.min(1, c[al] / N) : 0, ov: !!c.excess && sz !== 'excess', so: Math.min(1, (c.excess || 0) / N) })
    }
    const D = 3.3, TP = 6, ease = (x) => x * x * (3 - 2 * x)
    const branch = (has, sign, colOf, share) => {
        const out = [], runs = []
        let a = null
        smp.forEach((p, j) => { if (has(p)) { if (a == null) a = j } else if (a != null) { runs.push([a, j - 1]); a = null } })
        if (a != null) runs.push([a, smp.length - 1])
        for (const [i0, i1] of runs) {
            const xa = smp[i0].x, xb = smp[i1].x, ea = i0 === 0, eb = i1 === smp.length - 1
            const pts = smp.slice(i0, i1 + 1).map(p => {
                const ta = ea ? 1 : Math.min(1, (p.x - xa) / TP), tb = eb ? 1 : Math.min(1, (xb - p.x) / TP)
                return Object.assign({}, p, { y: Math.max(1.2, Math.min(25.4, p.ys + sign * D * ease(Math.max(0, ta)) * ease(Math.max(0, tb)))) })
            })
            if (!ea) pts.unshift(Object.assign({}, smp[i0], { y: smp[i0].ys }))
            if (!eb) pts.push(Object.assign({}, smp[i1], { y: smp[i1].ys }))
            for (const pc of piecesBy(pts, p => colOf(p) + '|' + (calm ? Math.round(share(p) * 4) : 0))) {
                const sh = share(pc.s), col = pc.k.split('|')[0]
                out.push(poly(pc.pts, col, calm ? 0.7 + 1.5 * sh : 1.6, calm ? 0.5 + 0.5 * sh : 1))
            }
        }
        return out
    }
    const alCol = (p) => hue(p.al === 'oos' ? 'oos' : 'min', calm, total)
    return { layers: [...base.layers, ...branch(p => !!p.al, +1, alCol, p => p.sa || 0), ...branch(p => p.ov, -1, () => hue('excess', calm, total), p => p.so || 0)],
        title: base.title || titleOf(cell) }
}

// режимы для STOCK_GROUP_MODES (функции-объявления: вызывается при инициализации stockCells.js, до вычисления этого модуля в цикле импорта)
export function stockGroupModes2_modes() { return { g1, g2, g3, g4, g5 } }
// калм-флаг из читателя настроек (строки 'false' / '0' / 'off' хеша пробы — выкл)
export function stockGroupCalm_read(rd) {
    const v = rd(STOCK_GROUP_CALM_FLAG, STOCK_GROUP_CALM_DEFAULT)
    return !(v === false || v === 0 || v === 'false' || v === '0' || v === 'off')
}
// цветное чорнило слоёв (длина × толщина × прозрачность линий OOS / Critical) — для тестов приглушения
export function stockG_alertInk(layers) {
    let s = 0
    for (const l of layers || []) {
        const m = /^rgba\((\d+),(\d+),(\d+),([\d.]+)\)$/.exec(String(l.stroke || '')), op = m ? Number(m[4]) : (l.op && l.op < 1 ? l.op : 1)
        const base = m ? '#' + [m[1], m[2], m[3]].map(x => Number(x).toString(16).padStart(2, '0')).join('') : String(l.stroke || '')
        if (!isAlertHue(base.toLowerCase())) continue
        let len = 0
        if (l.k === 'line') len = Math.hypot(l.x2 - l.x1, l.y2 - l.y1)
        else if (l.k === 'poly') for (let j = 1; j < l.pts.length; j++) len += Math.hypot(l.pts[j][0] - l.pts[j - 1][0], l.pts[j][1] - l.pts[j - 1][1])
        s += len * (Number(l.sw) || 0) * op
    }
    return s
}
